import React, { FC, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, IconButton, Typography, useThemeValue } from '@altermaind/web/ui';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { CollapsableCard } from '../CollapsableCard';
import {
  AreaProgressBarBg,
  AreaProgressBarFg,
  AreaProgressWrapper,
  AreasBar,
  MacroAreaDot,
} from './styles';
import { WizardProps } from './types';
import { useWizardNavigation } from './useWizardNavigation';
import { useWizardProgress } from './useWizardProgress';

/**
 * Wizard Component
 *
 * This component renders a multi-step wizard interface, allowing users to navigate through steps and micro-steps.
 * It provides navigation buttons (Next, Previous, Skip) and visual progress indicators for each step.
 *
 * @param {WizardProps} props - The properties for the Wizard component.
 * @param {Array} props.stepsConfig - Configuration for the wizard steps. Each step defines its label, progress, and other metadata.
 *
 * @returns {JSX.Element} The rendered wizard component.
 *
 * ### Dependencies:
 * - `useTranslations`: Hook for retrieving localized strings.
 * - `useWizardNavigation`: Custom hook for managing wizard navigation logic.
 * - `useWizardProgress`: Custom hook for calculating progress across steps.
 * - `useThemeValue`: Hook for accessing theme values (e.g., sizes).
 * - `useMemo`: React hook for memoizing computed values.
 * - Components: `Box`, `Typography`, `MacroAreaDot`, `AreaProgressWrapper`, `AreaProgressBarBg`, `AreaProgressBarFg`, `AreasBar`, `IconButton`, `Button`.
 *
 * ### Example Usage:
 * ```jsx
 * const stepsConfig = [
 *   { label: 'Step 1', steps: [
 *       { label: 'Colors' },
 *       { label: 'Notifications' },
 *       { label: 'Role' },
 *       { label: 'Extra Options' },
 *   ]},
 *   { label: 'Step 2', ... },
 *   { label: 'Step 3', ... },
 * ];
 *
 * <Wizard stepsConfig={stepsConfig} />
 * ```
 */
export const Wizard: FC<WizardProps> = ({ stepsConfig }) => {
  const t = useTranslations('wizard');

  const { genericResponsiveValue } = useResponsiveStyle();
  const { isMobile } = useBreakingPoints();

  const {
    activeStep,
    activeMicroStepData,
    isLoadingNextButton,
    disableNextButton,
    disablePreviousButton,
    disableSkipButton,
    handlePrevious,
    handleNext,
    handleSkip,
  } = useWizardNavigation({
    stepsConfig,
  });

  const areaProgress = useWizardProgress({ stepsConfig, activeStep });

  const gap = parseFloat(`${useThemeValue('sizes', 'xs')}`) || 10;

  const showSkipButton = useMemo(() => {
    return activeMicroStepData?.showSkipButton || !!activeMicroStepData?.onSkip;
  }, [activeMicroStepData]);

  const showPreviousButtonBeforeProgressBar = useMemo(() => {
    return activeMicroStepData?.showPreviousButtonBeforeProgressBar;
  }, [activeMicroStepData]);

  const showPreviousButton = useMemo(() => {
    return (
      activeMicroStepData?.forceShowPreviousButton ||
      (activeMicroStepData?.showPreviousButton !== false && activeStep > 0)
    );
  }, [
    activeMicroStepData?.forceShowPreviousButton,
    activeMicroStepData?.showPreviousButton,
    activeStep,
  ]);

  const showNextButton = useMemo(() => {
    return activeMicroStepData?.showNextButton !== false; // default to true
  }, [activeMicroStepData]);

  const NavigationButton = useMemo(() => {
    // eslint-disable-next-line react/no-unstable-nested-components
    const Next = () => (
      <Button
        data-testid='wizard-next-button'
        variant='primary'
        onClick={handleNext}
        loading={isLoadingNextButton}
        disabled={disableNextButton}
        spinnerColor='white'
        spinnerStyle={{ size: 's' }}
      >
        {activeMicroStepData.nextButtonLabel || t('nextButton')}
      </Button>
    );
    // eslint-disable-next-line react/no-unstable-nested-components
    const Skip = () => (
      <Button
        variant='link/primary'
        onClick={handleSkip}
        style={{ textDecoration: 'none', cursor: 'pointer' }}
        disabled={disableSkipButton}
        spinnerColor='white'
      >
        {activeMicroStepData.skipButtonLabel || t('skipButton')}
      </Button>
    );

    // eslint-disable-next-line react/no-unstable-nested-components
    const Previous = () => (
      <IconButton
        prefixSvg='chevronLeft'
        variant='link/primary'
        onClick={handlePrevious}
        style={{ textDecoration: 'none', cursor: 'pointer' }}
        svgStyle={{ width: 'xs' }}
        textStyle={{ variant: 'p2/light', marginLeft: 'xxxxs' }}
        disabled={disablePreviousButton}
      >
        {activeMicroStepData.previousButtonLabel || t('previousButton')}
      </IconButton>
    );
    return { Next, Previous, Skip };
  }, [
    activeMicroStepData.nextButtonLabel,
    activeMicroStepData.previousButtonLabel,
    activeMicroStepData.skipButtonLabel,
    disableNextButton,
    disablePreviousButton,
    disableSkipButton,
    handleNext,
    handlePrevious,
    handleSkip,
    isLoadingNextButton,
    t,
  ]);

  const renderComponent =
    typeof activeMicroStepData?.component === 'function'
      ? activeMicroStepData?.component({ wizardNavigationButton: NavigationButton })
      : activeMicroStepData?.component;

  return (
    <Box
      px={genericResponsiveValue({
        breakPoints: { desktop: 'xs' },
        defaultValue: 'none',
      })}
      display='flex'
      flexDirection='column'
    >
      {!activeMicroStepData.isTyp && (
        <Box display='flex'>
          {showPreviousButton && showPreviousButtonBeforeProgressBar && (
            <NavigationButton.Previous />
          )}
        </Box>
      )}

      {stepsConfig.length > 1 && !activeMicroStepData.isTyp && (
        <Box py='m' margin='auto' width={isMobile ? '100%' : 'auto'}>
          {isMobile && (
            <CollapsableCard
              title={
                stepsConfig.filter((area, macroIdx) => {
                  const { isAreaActivated, areaCompleted } = areaProgress[macroIdx];
                  if (isAreaActivated && !areaCompleted) {
                    return area;
                  }
                  return null;
                })[0].label || ''
              }
            >
              <AreasBar>
                {stepsConfig.map((area, macroIdx) => {
                  const { percent, areaCompleted, isAreaActivated, areaStepsCount } =
                    areaProgress[macroIdx];

                  return (
                    <Box
                      // eslint-disable-next-line react/no-array-index-key
                      key={`macro-step-${area.label}-${macroIdx}`}
                      flex={3}
                      textAlign='center'
                      width='100%'
                      mt='s'
                    >
                      <Typography
                        variant='p2/bold'
                        mb='xxxs'
                        style={{
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {area.label}
                      </Typography>
                      <MacroAreaDot highlight={areaCompleted} active={isAreaActivated} />
                      <AreaProgressWrapper steps={areaStepsCount}>
                        <AreaProgressBarBg active={isAreaActivated}>
                          <AreaProgressBarFg percent={percent} />
                        </AreaProgressBarBg>
                      </AreaProgressWrapper>
                    </Box>
                  );
                })}
              </AreasBar>
            </CollapsableCard>
          )}
          {!isMobile && (
            <AreasBar>
              {stepsConfig.map((area, macroIdx) => {
                const { percent, areaFlexColumn, areaCompleted, isAreaActivated, areaStepsCount } =
                  areaProgress[macroIdx];

                return (
                  <Box
                    // eslint-disable-next-line react/no-array-index-key
                    key={`macro-step-${area.label}-${macroIdx}`}
                    flex={areaFlexColumn}
                    textAlign='center'
                  >
                    <Typography
                      variant='p2/bold'
                      mb='xxxs'
                      style={{
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {area.label}
                    </Typography>
                    <MacroAreaDot highlight={areaCompleted} active={isAreaActivated} />
                    <AreaProgressWrapper steps={areaStepsCount}>
                      <AreaProgressBarBg active={isAreaActivated}>
                        <AreaProgressBarFg percent={percent} />
                      </AreaProgressBarBg>
                    </AreaProgressWrapper>
                  </Box>
                );
              })}
            </AreasBar>
          )}
        </Box>
      )}

      {!activeMicroStepData.isTyp && (
        <Box display='flex'>
          {showPreviousButton && !showPreviousButtonBeforeProgressBar && (
            <NavigationButton.Previous />
          )}
        </Box>
      )}

      <Box
        py={genericResponsiveValue({
          breakPoints: { desktop: 'm' },
          defaultValue: 'none',
        })}
      >
        {/* Render the micro step component */}
        {renderComponent}
      </Box>

      {!activeMicroStepData.isTyp && (
        <Box display='flex' style={{ gap }}>
          {showNextButton && <NavigationButton.Next />}
          {showSkipButton && <NavigationButton.Skip />}
        </Box>
      )}
    </Box>
  );
};
